import {
  Box,
  Card,
  Chip,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { JSONContent } from "@tiptap/react";
import { useMemo, useState } from "react";
import { FiEye, FiList } from "react-icons/fi";
import { IoClose } from "react-icons/io5";

import { Categorie } from "../../../apiServices/categories.service";
import ArticleDetailContent from "../../blog/components/Article";
import ArticleCard from "../../blog/components/ArticleCard";
import { Article } from "../../blog/services/mockData";
import {
  calculateReadingTime,
  generateSummary,
} from "../helpers/article.helpers";

const previewTheme = createTheme({
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

interface AdminArticlePreviewProps {
  open: boolean;
  onClose: () => void;
  title: string;
  content: JSONContent;
  imagePreview?: string;
  categoryId: string;
  status: string;
  categories: Categorie[];
  type: string;
  resumen: string;
}

const STATUS_LABELS: Record<
  string,
  { label: string; color: "secondary" | "warning" | "info" | "success" | "error" }
> = {
  BORRADOR: { label: "Borrador", color: "secondary" },
  EN_REVISION: { label: "En revisión", color: "warning" },
  APROBADO: { label: "Aprobado", color: "info" },
  PUBLICADO: { label: "Publicado", color: "success" },
};

function formatDate(date: Date): string {
  return date.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function AdminArticlePreview({
  open,
  onClose,
  title,
  content,
  imagePreview,
  categoryId,
  status,
  categories,
  type,
  resumen,
}: AdminArticlePreviewProps) {
  const [activeTab, setActiveTab] = useState(0);

  const categoryName = useMemo(() => {
    const cat = categories.find(
      (c) => String(c.categoriaId) === String(categoryId),
    );
    return (cat?.nombreEspanol ?? "Sin categoría").toUpperCase();
  }, [categories, categoryId]);

  const excerpt = useMemo(() => resumen.trim() || generateSummary(content), [resumen, content]);
  const readingTime = useMemo(() => calculateReadingTime(content), [content]);
  const today = useMemo(() => formatDate(new Date()), []);
  const statusInfo = STATUS_LABELS[status] ?? {
    label: status || "Sin estado",
    color: "default" as const,
  };

  const displayTitle = title.trim() || "Sin título";

  const previewArticle = useMemo<Article>(
    () => ({
      id: 0,
      title: displayTitle,
      excerpt,
      content,
      image: imagePreview,
      category: categoryName,
      tag: type === "NOTICIAS_IT" ? "Noticias IT" : "Temas CG",
      author: "Equipo de CG",
      authorAvatar: "CG",
      date: today,
    }),
    [displayTitle, excerpt, content, imagePreview, categoryName, today, type],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      slotProps={{
        paper: {
          sx: { bgcolor: "#f8fafc", display: "flex", flexDirection: "column" },
        },
      }}
    >
      {/* ── Header ── */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, sm: 3 },
          gap: 2,
          flexShrink: 0,
        }}
      >
        <FiEye size={20} color="#0069a7" />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "text.primary", flexGrow: 1 }}
        >
          Vista Previa
        </Typography>
        <Chip
          label={statusInfo.label}
          color={statusInfo.color}
          size="small"
          sx={{ fontWeight: 600, textTransform: "none" }}
        />
        <IconButton
          onClick={onClose}
          edge="end"
          aria-label="Cerrar vista previa"
        >
          <IoClose size={22} />
        </IconButton>
      </Toolbar>

      {/* ── Warning banner ── */}
      <Box
        sx={{
          bgcolor: "#fefce8",
          borderBottom: "1px solid #fde047",
          px: 3,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "#854d0e", fontWeight: 600 }}
        >
          ⚠️ Vista previa — Este artículo
          {status === "PUBLICADO"
            ? " ya está publicado."
            : " aún no está publicado."}{" "}
          Los cambios no guardados no se reflejarán en el sitio.
        </Typography>
      </Box>

      {/* ── Tabs ── */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: { xs: 2, sm: 3 } }}
        >
          <Tab
            icon={<FiList size={16} />}
            iconPosition="start"
            label="Tarjeta en el listado"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
          />
          <Tab
            icon={<FiEye size={16} />}
            iconPosition="start"
            label="Detalle del artículo"
            sx={{ textTransform: "none", fontWeight: 600, minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{ p: 0, flex: 1, overflow: "auto" }}>
        <ThemeProvider theme={previewTheme}>
          {/* TAB 0 — Card en el listado */}
          {activeTab === 0 && (
             <Box
              sx={{
                minHeight: '100%',
                bgcolor: '#ffffff',
                py: { xs: 4, md: 6 },
                px: { xs: 2, sm: 4 },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: "25px",
                }}
              >
                <ArticleCard article={previewArticle} disabledNavigation />

                {[1, 2].map((i) => (
                  <Card
                    key={i}
                    elevation={0}
                    sx={{
                      display: { xs: "none", md: "block" },
                      height: "420px",
                      borderRadius: "15px",
                      border: "1px dashed #e2e8f0",
                      backgroundColor: "#f8fafc",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* TAB 1 — Detalle del artículo */}
          {activeTab === 1 && (
            <Box
              sx={{
                minHeight: '100%',
                bgcolor: '#ffffff',
                py: { xs: 4, md: 6 },
              }}
            >
              <Container
                maxWidth="md"
                sx={{
                  pb: 8,
                }}
              >
                <ArticleDetailContent article={previewArticle} />
              </Container>
            </Box>
          )}
        </ThemeProvider>
      </DialogContent>
    </Dialog>
  );
}
